import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import "../../css/AnimChatbotGen.css";
import AnimBadge from "../common/AnimBadge";

// Safely resolve a nested value from "a.b.c" paths.
const resolvePath = (source, path) => {
  if (!source || !path) return undefined;
  return path.split(".").reduce((acc, segment) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[segment];
  }, source);
};

export default function AnimChatbotGen({
  config,
  context = {},
  actions = {},
  logicGuards = {},
  popinComponents = {},
  templates = {},
}) {
  const getStartNodeId = useCallback(() => {
    const nodes = config?.nodes || {};
    const startEntry = Object.entries(nodes).find(([, node]) => node?.isStart);
    if (startEntry?.[0]) return startEntry[0];
    return config?.metadata?.startNode || config?.startNode;
  }, [config]);
  const [currentStateId, setCurrentStateId] = useState(() =>
    getStartNodeId()
  );
  const [history, setHistory] = useState([]);
  const [activeUtterance, setActiveUtterance] = useState(null);
  const [inputText, setInputText] = useState("");
  const [videoSrc, setVideoSrc] = useState("");
  const [waitingActive, setWaitingActive] = useState(false);
  const [revealedText, setRevealedText] = useState("");
  const [isRevealing, setIsRevealing] = useState(false);
  const [badgeVisible, setBadgeVisible] = useState(false);
  const [dismissedBadgeId, setDismissedBadgeId] = useState(null);
  const [stateHistory, setStateHistory] = useState([]);
  const [dynamicContext, setDynamicContext] = useState({});

  const [activePopins, setActivePopins] = useState({});

  const videoRef = useRef(null);
  const scrollRef = useRef(null);
  const revealRafRef = useRef(null);
  const pendingVideoActionRef = useRef(null);
  const waitingIndexRef = useRef(0);
  const historyIdRef = useRef(0);
  const prevStateRef = useRef("INIT");
  const prevWaitingRef = useRef(false);
  const historyRef = useRef([]);
  const actionsRef = useRef(actions || {});
  const logicGuardsRef = useRef(logicGuards || {});
  const popinComponentsRef = useRef(popinComponents || {});

  // Generate unique ids for history entries and states.
  const nextId = useCallback((prefix = "msg") => {
    historyIdRef.current += 1;
    return `${prefix}_${Date.now()}_${historyIdRef.current}`;
  }, []);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    actionsRef.current = actions || {};
  }, [actions]);

  useEffect(() => {
    logicGuardsRef.current = logicGuards || {};
  }, [logicGuards]);

  useEffect(() => {
    popinComponentsRef.current = popinComponents || {};
  }, [popinComponents]);

  useEffect(() => {
    setDynamicContext({});
  }, [context]);

  useEffect(() => {
    setActivePopins({});
  }, [config]);

  useEffect(() => {
    setCurrentStateId(getStartNodeId());
  }, [getStartNodeId]);

  // Expected context: plain object with all keys needed by templates/guards/actions.
  const contextVariables = useMemo(
    () => ({ ...(context || {}), ...(dynamicContext || {}) }),
    [context, dynamicContext]
  );

  const mergedTemplates = useMemo(
    () => ({
      ...(templates || {}),
      ...(config.templates || {}),
    }),
    [config, templates]
  );

  // Merge dynamic context updates coming from actions.
  const updateDynamicContext = useCallback((patch) => {
    setDynamicContext((prev) => {
      const nextPatch =
        typeof patch === "function" ? patch(prev || {}) : patch || {};
      if (
        !nextPatch ||
        typeof nextPatch !== "object" ||
        Array.isArray(nextPatch)
      ) {
        return prev || {};
      }
      return { ...(prev || {}), ...nextPatch };
    });
  }, []);

  // Keep history state and ref in sync from one place.
  const setHistoryWith = useCallback((updater) => {
    setHistory((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : updater || [];
      historyRef.current = next;
      return next;
    });
  }, []);

  // Add one history entry with normalization.
  const appendHistoryEntry = useCallback(
    (entry) => {
      if (!entry || !entry.role || entry.text === undefined) return;
      const normalized = {
        ...entry,
        id: entry.id || nextId(entry.role === "user" ? "user" : "evt"),
        text: `${entry.text}`, // force string for rendering
      };
      setHistoryWith((prev) => [...prev, normalized]);
    },
    [nextId, setHistoryWith]
  );

  // Add a batch of history entries with normalization.
  const appendHistoryEntries = useCallback(
    (entries) => {
      if (!Array.isArray(entries) || !entries.length) return;
      setHistoryWith((prev) => {
        const normalized = entries
          .filter((entry) => entry && entry.role && entry.text !== undefined)
          .map((entry) => ({
            ...entry,
            id:
              entry.id ||
              nextId(entry.role === "user" ? "user" : "evt"),
            text: `${entry.text}`,
          }));
        return [...prev, ...normalized];
      });
    },
    [nextId, setHistoryWith]
  );

  const baseVideoUrl = config.metadata?.videoBaseUrl || "";
  const waitingPlaylist = useMemo(
    () => config.videoMap?.waiting || [],
    [config.videoMap]
  );

  // Replace {vars} in templates using the resolved context variables.
  const interpolate = useCallback(
    (template = "") =>
      template.replace(/\{([^}]+)\}/g, (_, key) =>
        contextVariables[key] !== undefined ? contextVariables[key] : ""
      ),
    [contextVariables]
  );

  // Rotate through the waiting playlist.
  const playNextWaitingVideo = useCallback(() => {
    if (!waitingPlaylist.length) return;
    const idx = waitingIndexRef.current % waitingPlaylist.length;
    waitingIndexRef.current = (idx + 1) % waitingPlaylist.length;
    setVideoSrc(`${baseVideoUrl}${waitingPlaylist[idx]}`);
  }, [waitingPlaylist, baseVideoUrl]);

  // Apply the node video cue, or fall back to waiting clips.
  const setVideoFromNode = useCallback(
    (node) => {
      const mappedVideo =
        (node?.videoCue && config.videoMap?.[node.videoCue]) || null;
      if (mappedVideo) {
        setVideoSrc(`${baseVideoUrl}${mappedVideo}`);
      } else if (waitingPlaylist.length) {
        playNextWaitingVideo();
      } else {
        setVideoSrc("");
      }
    },
    [baseVideoUrl, config.videoMap, playNextWaitingVideo, waitingPlaylist.length]
  );

  const popinNodeMap = useMemo(() => {
    const entries = config.popins || {};
    const map = {};
    Object.entries(entries).forEach(([key, definition]) => {
      const nodeId =
        definition?.nodeId ||
        definition?.node ||
        definition?.triggerNode ||
        definition?.trigger;
      if (nodeId) map[nodeId] = key;
    });
    return map;
  }, [config]);
  const popinDefinitions = useMemo(() => config.popins || {}, [config]);

  // Intercept special nodes that open popins instead of transitioning.
  const handleSpecialNode = useCallback(
    (nodeId) => {
      const popinKey = popinNodeMap[nodeId];
      if (popinKey) {
        setActivePopins((prev) => ({
          ...prev,
          [popinKey]: true,
        }));
        return true;
      }
      return false;
    },
    [popinNodeMap]
  );

  const getNodeBranches = useCallback(
    (node) => {
      if (!node) return [];
      return node.branches || node.conditions || node.transitions || [];
    },
    []
  );

  const evaluateBranchCondition = useCallback(
    (branch, node) => {
      if (!branch) return false;
      if (branch.guard && logicGuardsRef.current[branch.guard]) {
        try {
          return Boolean(
            logicGuardsRef.current[branch.guard]({
              context: contextVariables,
              node,
              config,
              history: historyRef.current.slice(),
            })
          );
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(
            `Guard "${branch.guard}" failed in node ${node?.id || "?"}`,
            error
          );
          return false;
        }
      }
      if (branch.if !== undefined) {
        if (Array.isArray(branch.if)) {
          return branch.if.some(
            (path) => resolvePath(contextVariables, path) === true
          );
        }
        if (typeof branch.if === "string") {
          return resolvePath(contextVariables, branch.if) === true;
        }
      }
      return Boolean(branch.else);
    },
    [config, contextVariables]
  );

  // --- Moteur de Transition ---
  // Core state transition engine.
  const transitionTo = useCallback(
    (nodeId) => {
      if (!nodeId) return;
      if (handleSpecialNode(nodeId)) return;

      const node = config.nodes[nodeId];
      if (!node) return;

      // Résolution de la logique invisible (ex: orientation vers S4A ou S4B) [cite: 2]
      if (node.type === "LOGIC") {
        const previous = prevStateRef.current || currentStateId;
        setStateHistory((prevList) => [
          ...prevList,
          {
            id: nextId("state"),
            from: previous,
            to: nodeId,
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
        prevStateRef.current = nodeId;
        const branch = getNodeBranches(node).find((entry) =>
          evaluateBranchCondition(entry, node)
        );
        if (branch?.next) return transitionTo(branch.next);
        return;
      }

      const previous = prevStateRef.current || currentStateId;
      setStateHistory((prevList) => [
        ...prevList,
        {
          id: nextId("state"),
          from: previous,
          to: nodeId,
          timestamp: new Date().toLocaleTimeString(),
        },
      ]);
      prevStateRef.current = nodeId;
      setCurrentStateId(nodeId);

      // Mise à jour Vidéo
      setVideoFromNode(node);

      // Préparation du message Bot
      if (node.text) {
        const text = interpolate(node.text);
      const utterance = {
        id: nextId("utt"),
        text,
        botRefHtml: (() => {
          const template =
            (node.botRefHtml && mergedTemplates[node.botRefHtml]) ||
            node.botRefHtml ||
            null;
          return template ? interpolate(template) : null;
        })(),
          badge: node.badge
            ? {
                title: node.badge.title,
                message: interpolate(node.badge.message),
                annotation: node.badge.annotation
                  ? interpolate(node.badge.annotation)
                  : "",
                triggerPhrase: node.badge.triggerPhrase || "",
              }
            : null,
        };

        setActiveUtterance(utterance);
        setWaitingActive(false);
        setBadgeVisible(false);
      } else {
        setActiveUtterance(null);
        setBadgeVisible(false);
        setWaitingActive(true);
        if (node.autoNext && node.nextDefault) {
          transitionTo(node.nextDefault);
        }
      }

      if (node.type === "ACTION" && node.actionName) {
        const handler = actionsRef.current[node.actionName];
        if (typeof handler === "function") {
          Promise.resolve(
            handler({
              node,
              config,
              context: contextVariables,
              transitionTo,
              addHistoryEntry: appendHistoryEntry,
              getHistory: () => [...historyRef.current],
              setContext: updateDynamicContext,
              setWaiting: setWaitingActive,
              nextId,
              interpolate,
              guards: logicGuardsRef.current,
            })
          ).catch((error) => {
            // eslint-disable-next-line no-console
            console.error(
              `Action "${node.actionName}" failed in node ${nodeId}`,
              error
            );
          });
        }
      }
    },
    [
      config,
      contextVariables,
      interpolate,
      handleSpecialNode,
      currentStateId,
      evaluateBranchCondition,
      getNodeBranches,
      nextId,
      appendHistoryEntry,
      updateDynamicContext,
      mergedTemplates,
      setVideoFromNode,
    ]
  );

  useEffect(() => {
    transitionTo(getStartNodeId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getStartNodeId]);

  
  const recentStateHistory = useMemo(
    () => stateHistory.slice(-10).reverse(),
    [stateHistory]
  );

  // Auto-scroll du chat
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [history, revealedText, isRevealing]);

  useEffect(() => {
    if (waitingActive && !prevWaitingRef.current && waitingPlaylist.length) {
      const node = config.nodes?.[currentStateId];
      if (node?.type !== "WAITING") {
        playNextWaitingVideo();
      }
    }
    prevWaitingRef.current = waitingActive;
  }, [waitingActive, waitingPlaylist.length, playNextWaitingVideo, currentStateId, config]);

  // Révélation progressive
  useEffect(() => {
    if (!activeUtterance?.id) {
      setRevealedText("");
      setIsRevealing(false);
      return;
    }
    if (revealRafRef.current) cancelAnimationFrame(revealRafRef.current);

    const text = activeUtterance.text || "";
    if (!text) {
      setRevealedText("");
      setIsRevealing(false);
      return;
    }

    setIsRevealing(true);
    setRevealedText("");
    const videoDurationMs = Number.isFinite(videoRef.current?.duration)
      ? videoRef.current.duration * 1000
      : 0;
    const duration = Math.max(3000, text.length * 60, videoDurationMs);
    const start = performance.now();
    const runAfterVideo = (action) => {
      const video = videoRef.current;
      if (video && !video.ended && !video.paused) {
        pendingVideoActionRef.current = action;
        return;
      }
      action();
    };

    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const count = Math.max(1, Math.floor(text.length * progress));
      setRevealedText(text.slice(0, count));
      if (progress < 1) {
        revealRafRef.current = requestAnimationFrame(step);
      } else {
        revealRafRef.current = null;
        setRevealedText(text);
        setIsRevealing(false);
        const newEntries = [
          { role: "bot", text },
          ...(activeUtterance.botRefHtml
            ? [{ role: "botref", text: activeUtterance.botRefHtml }]
            : []),
        ];
        appendHistoryEntries(newEntries);
        runAfterVideo(() => {
          setWaitingActive(true);
          const currentNode = config.nodes?.[currentStateId];
          if (currentNode?.autoNext && currentNode.nextDefault) {
            transitionTo(currentNode.nextDefault);
          }
        });
      }
    };
    revealRafRef.current = requestAnimationFrame(step);

    return () => {
      if (revealRafRef.current) cancelAnimationFrame(revealRafRef.current);
    };
  }, [activeUtterance, appendHistoryEntries, config, currentStateId, transitionTo]);

  // Prevent the same badge from reappearing after dismiss.
  const handleBadgeClose = useCallback(() => {
    setBadgeVisible(false);
    if (activeUtterance?.id) {
      setDismissedBadgeId(activeUtterance.id);
    }
  }, [activeUtterance?.id]);

  // Badge trigger on reveal
  useEffect(() => {
    setBadgeVisible(false);
    setDismissedBadgeId(null);
  }, [activeUtterance?.id]);

  useEffect(() => {
    if (!activeUtterance?.badge || badgeVisible) return;
    if (dismissedBadgeId && dismissedBadgeId === activeUtterance.id) return;
    const trigger = activeUtterance.badge.triggerPhrase?.trim();
    if (!trigger) {
      setBadgeVisible(true);
      return;
    }
    const normalized = (revealedText || "").toLowerCase();
    if (normalized.includes(trigger.toLowerCase())) {
      setBadgeVisible(true);
    }
  }, [revealedText, activeUtterance?.badge, activeUtterance?.id, badgeVisible, dismissedBadgeId]);

  // --- Handlers ---
  // User sends a free-text message.
  const handleSend = () => {
    if (!inputText.trim()) return;
    setWaitingActive(false);
    appendHistoryEntry({ role: "user", text: inputText });
    setInputText("");

    const node = config.nodes[currentStateId];
    if (node.nextDefault) transitionTo(node.nextDefault);
  };

  // User clicks an option from botRefHtml.
  const handleOptionClick = (e) => {
    const target = e.target.closest("[data-option]");
    if (!target) return;
    e.preventDefault();
    const option = target.dataset.option;
    if (!option) return;

    //setWaitingActive(false);

    const node = config.nodes[currentStateId];
    const transition = getNodeBranches(node).find((t) => t.on === option);
    if (transition) transitionTo(transition.next);
  };

  return (
    <>
      <div className="mas-page-wrapper">
        <div className="mas-chat-container">
          <div className="mas-main-layout">
            <div className="mas-video-section">
            {activeUtterance?.badge && badgeVisible && (
              <div className="bonus-badge-wrapper">
                <AnimBadge
                  title={activeUtterance.badge.title}
                  message={activeUtterance.badge.message}
                  annotation={activeUtterance.badge.annotation}
                  onClose={handleBadgeClose}
                />
              </div>
            )}
            {videoSrc ? (
              <video
                ref={videoRef}
                src={videoSrc}
                autoPlay
                muted={false}
                playsInline
                className="mas-video-element"
                onEnded={() => {
                  if (pendingVideoActionRef.current) {
                    const action = pendingVideoActionRef.current;
                    pendingVideoActionRef.current = null;
                    action();
                  }
                  if (waitingActive) {
                    playNextWaitingVideo();
                  }
                }}
              />
            ) : null}
            <div className="mas-desktop-only-subtitles">
              {revealedText}
            </div>
          </div>

            <div className="mas-interaction-section">
            <div className="mas-chat-history" ref={scrollRef}>
              {history.map((m) => {
                const isBotRef = m.role === "botref";
                return (
                  <div key={m.id} className={`msg-wrap ${m.role}`}>
                    {isBotRef ? (
                      <div className="bot-ref-container options-bubble" onClick={handleOptionClick}>
                        <div dangerouslySetInnerHTML={{ __html: m.text }} />
                      </div>
                    ) : (
                      <div className="bubble">
                        <span dangerouslySetInnerHTML={{ __html: m.text }} />
                      </div>
                    )}
                  </div>
                );
              })}
              {isRevealing && revealedText && (
                <div className="msg-wrap bot ghost">
                  <div className="bubble">{revealedText}</div>
                </div>
              )}
            </div>

            <div className="mas-input-area">
              <input
                className="mas-text-input"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Écrivez votre réponse..."
              />
              <button className="mas-send-btn" onClick={handleSend}>
                🎙️
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mas-tech-panel">
          <div className="tech-card">
            <div><strong>État courant :</strong> {currentStateId}</div>
            <div><strong>Attente active :</strong> {waitingActive ? "OUI" : "NON"}</div>
            <div>
              <strong>Utterance active :</strong>{" "}
              {activeUtterance ? activeUtterance.id : "—"}
            </div>
            <div className="tech-section">
              <strong>Transitions (dern. 10) :</strong>
              <ul>
                {recentStateHistory.map((item) => (
                  <li key={item.id}>
                    [{item.timestamp}] {item.from} ➜ {item.to}
                  </li>
                ))}
                {!recentStateHistory.length && <li>Aucune transition</li>}
              </ul>
            </div>
          </div>
      </div>
      {Object.entries(popinComponentsRef.current).map(
        ([key, ModalComponent]) => {
          if (!ModalComponent) return null;
          const popinDefinition = popinDefinitions?.[key] || {};
          return (
            <ModalComponent
              key={key}
              isOpen={Boolean(activePopins[key])}
              onClose={() =>
                setActivePopins((prev) => ({
                  ...prev,
                  [key]: false,
                }))
              }
              onPopinConfirm={(payload) => {
                setActivePopins((prev) => ({
                  ...prev,
                  [key]: false,
                }));
                if (payload?.contextPatch) {
                  updateDynamicContext(payload.contextPatch);
                }
                const nextNode =
                  payload?.nextNode || popinDefinition.nextOnConfirm;
                if (nextNode) {
                  setTimeout(() => {
                    transitionTo(nextNode);
                  }, 0);
                }
              }}
            />
          );
        }
      )}
    </>
  );
}
